import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Badge, Button, ConfirmDialog, ErrorPanel, LoadingState, SortableTh } from '../../../school_panel/components/ui';
import { useNudgeTask, useTaskTracker } from '../../../school_panel/api/hooks';
import { useSortableTable } from '../../../school_panel/lib/useSortableTable';
import type { TaskTrackerRow, TrackerStatus } from '../../../school_panel/api/types';
import { EXAM_ORDER, NOTICE_ORDER, STATUS_LABEL, nudgeErrorMessage, nudgeMessage, studentsLabel } from '../nudgeCopy';
import { timeOf } from '../../../school_panel/lib/dates';

const TONE: Record<TrackerStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  done: 'success', late: 'warning', awaiting_marking: 'primary', missing: 'danger', not_yet: 'neutral', read: 'success', unread: 'neutral',
};

export function relativeTime(iso: string, now: Date = new Date()): string {
  const mins = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
}

function clock(iso: string): string {
  return timeOf(iso);
}

export default function TaskTrackerBody({ taskId }: { taskId: number }) {
  const q = useTaskTracker(taskId);
  const nudge = useNudgeTask();
  const [filter, setFilter] = useState<TrackerStatus | null>(null);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);

  const data = q.data;
  const order = data?.task.kind === 'notice' ? NOTICE_ORDER : EXAM_ORDER;

  const columns = useMemo(() => [
    { key: 'name', accessor: (r: TaskTrackerRow) => r.name, type: 'text' as const },
    { key: 'section', accessor: (r: TaskTrackerRow) => r.section, type: 'text' as const },
    { key: 'status', accessor: (r: TaskTrackerRow) => order.indexOf(r.status), type: 'number' as const },
    { key: 'at', accessor: (r: TaskTrackerRow) => r.at ?? '', type: 'date' as const },
  ], [order]);
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.rows ?? []).filter((r) => (!filter || r.status === filter) && (!needle || r.name.toLowerCase().includes(needle)));
  }, [data, filter, search]);
  const { rows, sort, toggle } = useSortableTable(visible, columns, { key: 'name', dir: 'asc' });

  if (q.isLoading) return <LoadingState label="Loading students…" />;
  if (q.isError || !data) return <ErrorPanel body="Could not load the tracker. Try again." onRetry={() => q.refetch()} />;

  const counts = new Map<TrackerStatus, number>();
  data.rows.forEach((r) => counts.set(r.status, (counts.get(r.status) ?? 0) + 1));
  const msg = nudgeMessage(data.task);

  const send = async () => {
    try {
      const res = await nudge.mutateAsync(taskId);
      toast.success('Reminder sent to ' + studentsLabel(res.sent));
    } catch (e) {
      toast.error(nudgeErrorMessage(e));
    } finally {
      setConfirm(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {order.filter((s) => (counts.get(s) ?? 0) > 0).map((s) => (
          <button key={s} type="button" data-testid={`tracker-chip-${s}`} aria-pressed={filter === s}
            onClick={() => setFilter(filter === s ? null : s)}
            className={`inline-flex min-h-[36px] items-center rounded-full border px-3 text-[13px] font-semibold transition-colors ${
              filter === s ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                : 'border-line-strong bg-surface text-muted-2 hover:text-ink'}`}>
            {`${STATUS_LABEL[s]} ${counts.get(s)}`}
          </button>
        ))}
      </div>
      <input type="search" aria-label="Search students" placeholder="Search students" value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="min-h-[40px] w-full rounded-[12px] border border-line-strong bg-surface px-3.5 text-sm text-ink outline-none focus:ring-2 focus:ring-accent" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <SortableTh columnKey="name" sort={sort} onToggle={toggle}>Name</SortableTh>
              <SortableTh columnKey="section" sort={sort} onToggle={toggle}>Section</SortableTh>
              <SortableTh columnKey="status" sort={sort} onToggle={toggle}>Status</SortableTh>
              <SortableTh columnKey="at" sort={sort} onToggle={toggle}>When</SortableTh>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.student_id} className="border-t border-line">
                <td className="px-2.5 py-2.5 font-medium text-ink">{r.name}</td>
                <td className="px-2.5 py-2.5 text-muted-2">{r.section}</td>
                <td className="px-2.5 py-2.5"><Badge tone={TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge></td>
                <td className="whitespace-nowrap px-2.5 py-2.5 text-xs text-muted-2">{r.at ? relativeTime(r.at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 border-t border-line pt-3">
        {data.last_nudge && (
          <p className="text-xs text-muted">
            Last nudged {relativeTime(data.last_nudge.at)} {data.last_nudge.by ? ` by ${data.last_nudge.by}` : ''} · {studentsLabel(data.last_nudge.recipients)}
          </p>
        )}
        {data.nudge_count > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={!data.can_nudge} onClick={() => setConfirm(true)}>Nudge {studentsLabel(data.nudge_count)}</Button>
            {!data.can_nudge && data.nudge_available_at && (
              <span className="text-xs text-muted">Available again at {clock(data.nudge_available_at)}</span>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog isOpen={confirm} danger={false} busy={nudge.isPending}
        title={msg.title} body={msg.body} confirmLabel="Confirm"
        onConfirm={send} onCancel={() => setConfirm(false)} />
    </div>
  );
}
